import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check, Plus } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { SOLUTIONS, SOL_IMG } from "@/data/content";
import { SOLUTION_DETAILS } from "@/data/solutions";
import { PageHero } from "@/components/shared/PageHero";
import { SectionHeading } from "@/components/shared/SectionHeading";
import { Reveal, Stagger, Item } from "@/components/shared/Reveal";
import { Icon } from "@/components/shared/Icon";
import { ClosingCTA } from "@/components/home/ClosingCTA";
import { getLenis } from "@/components/layout/SmoothScroll";

const COMBOS = [
  { stack: ["Fleet IoT", "Logistics", "AI"], text: "A connected delivery operation with live fleet data, automated dispatch and AI-driven performance insight." },
  { stack: ["School Transport", "CCTV", "Fleet IoT"], text: "A complete school transport stack: tracking, student identification, on-board video and parent communication." },
  { stack: ["CCTV", "AI", "Consulting"], text: "A safety programme built on video evidence, AI analytics and an independent technology roadmap." },
];

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  getLenis() ? getLenis().scrollTo(el, { offset: -150 }) : el.scrollIntoView({ behavior: "smooth" });
};

const JumpNav = ({ active }) => (
  <div data-testid="solutions-jump-nav" className="sticky top-[76px] z-30 border-y border-line bg-navy/85 backdrop-blur-xl">
    <div className="wrap flex gap-1 overflow-x-auto py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {SOLUTIONS.map((s) => (
        <button
          key={s.slug}
          type="button"
          onClick={() => scrollToId(s.slug)}
          data-testid={`solutions-jump-${s.slug}`}
          className={`flex shrink-0 items-center gap-2 rounded-full px-4 py-2 text-xs font-medium transition-colors ${active === s.slug ? "bg-cyan-400 text-navy" : "text-slate-300 hover:bg-white/[0.05] hover:text-white"}`}
        >
          <Icon name={s.icon} className="h-3.5 w-3.5" /> {SOLUTION_DETAILS[s.slug].formValue}
        </button>
      ))}
    </div>
  </div>
);

const SolutionRow = ({ s, i }) => {
  const d = SOLUTION_DETAILS[s.slug];
  const even = i % 2 === 0;
  return (
    <article id={s.slug} data-testid={`solutions-row-${s.slug}`} className="scroll-mt-40 border-b border-line py-16 last:border-b-0 lg:py-24">
      <div className={`grid items-start gap-10 lg:grid-cols-12 lg:gap-14 ${even ? "" : "lg:[&>*:first-child]:order-2"}`}>
        <Reveal className="lg:col-span-5">
          <div className="relative aspect-[4/3] overflow-hidden rounded-2xl border border-line">
            <img src={s.image} alt={s.title} className="h-full w-full object-cover transition-transform duration-700 hover:scale-105" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-transparent to-transparent" />
            <span className="absolute left-5 top-5 flex h-10 w-10 items-center justify-center rounded-xl border border-white/15 bg-navy/60 text-cyan-400 backdrop-blur"><Icon name={s.icon} className="h-5 w-5" /></span>
            <span className="absolute bottom-5 left-5 font-mono text-xs text-white/80">0{i + 1} / 06</span>
          </div>
        </Reveal>

        <div className="lg:col-span-7">
          <Reveal>
            <div className="eyebrow">{d.formValue}</div>
            <h2 className="mt-3 text-3xl font-semibold leading-tight sm:text-4xl">{s.title}</h2>
            <p className="mt-4 max-w-xl text-lg text-slate-300">{s.tagline}</p>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-slate-400">{d.intro[0]}</p>
          </Reveal>
          <Stagger className="mt-8 grid gap-3 sm:grid-cols-2" stagger={0.06}>
            {d.groups.map((g) => (
              <Item key={g.title} className="rounded-xl border border-line bg-navy-800/50 p-5">
                <div className="text-sm font-semibold text-white">{g.title}</div>
                <ul className="mt-3 space-y-1.5">
                  {g.items.slice(0, 3).map((it) => (
                    <li key={it} className="flex items-start gap-2 text-sm text-slate-400"><Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-cyan-400" />{it}</li>
                  ))}
                  {g.items.length > 3 && <li className="pl-5 font-mono text-[11px] text-slate-500">+{g.items.length - 3} more</li>}
                </ul>
              </Item>
            ))}
          </Stagger>
          <Reveal delay={0.1} className="mt-8 flex flex-wrap gap-3">
            <Link to={`/solutions/${s.slug}`} data-testid={`solutions-detail-link-${s.slug}`} className="btn-ghost group !py-2.5">{s.cta} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></Link>
            <Link to={`/contact?solution=${encodeURIComponent(d.formValue)}`} data-testid={`solutions-enquire-${s.slug}`} className="btn-primary !py-2.5">Enquire <ArrowUpRight className="h-4 w-4" /></Link>
          </Reveal>
        </div>
      </div>
    </article>
  );
};

export default function Solutions() {
  useSEO({ title: "Solutions | Fleet IoT, Logistics Automation, School Transport, CCTV & AI | Wexoz", description: "Explore Wexoz transportation technology solutions in Dubai and the UAE: IoT fleet management, logistics automation, school commute solutions, transport CCTV, transportation AI and technology consulting." });
  const { hash } = useLocation();
  const [active, setActive] = useState(null);

  useEffect(() => {
    if (hash) setTimeout(() => scrollToId(hash.slice(1)), 500);
  }, [hash]);

  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => e.isIntersecting && setActive(e.target.id));
    }, { rootMargin: "-40% 0px -50% 0px" });
    SOLUTIONS.forEach((s) => { const el = document.getElementById(s.slug); el && obs.observe(el); });
    return () => obs.disconnect();
  }, []);

  return (
    <>
      <PageHero eyebrow="Solutions" lines={["Best practices.", "One integrated stack."]} image={SOL_IMG.fleet}
        text="Each solution is a complete practice — hardware, software, connectivity and integration — designed to combine with the others into a single connected transportation operation." />

      <JumpNav active={active} />

      <section className="mx-auto w-full max-w-6xl px-6 lg:px-10">
        {SOLUTIONS.map((s, i) => <SolutionRow key={s.slug} s={s} i={i} />)}
      </section>

      <section className="section bg-[#F4F6FA] text-navy">
        <div className="mx-auto w-full max-w-6xl px-6 lg:px-10">
          <SectionHeading light eyebrow="Combined solutions" title="Solutions are designed to combine." text="Most operations need more than one practice. Because Wexoz designs the whole stack, solutions share devices, connectivity, platforms and integrations instead of duplicating them." />
          <Stagger className="mt-14 grid gap-5 md:grid-cols-3">
            {COMBOS.map((c, i) => (
              <Item key={i} className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
                <div className="flex flex-wrap items-center gap-2">
                  {c.stack.map((x, j) => (
                    <span key={x} className="flex items-center gap-2">
                      <span className="rounded-md bg-navy px-3 py-1.5 font-mono text-[11px] text-white">{x}</span>
                      {j < c.stack.length - 1 && <Plus className="h-3 w-3 text-cyan-600" />}
                    </span>
                  ))}
                </div>
                <p className="mt-5 text-sm text-slate-600">{c.text}</p>
              </Item>
            ))}
          </Stagger>
        </div>
      </section>
      <ClosingCTA />
    </>
  );
}
